<template>
  <div class="flex flex-col space-y-8">
    <div class="grid gap-4" :class="isGrossSales ? 'grid-cols-[repeat(5,1fr)]' : 'grid-cols-[repeat(3,1fr)]'">
      <div class="bg-white rounded-2xl shadow-xl border border-gray-100 py-5 px-5 flex flex-col gap-1.5">
        <p class="text-xs font-semibold uppercase tracking-widest text-gray-400">
          {{ t(`challenge.counting.${challenge.countingMethod}.column`) }}
        </p>
        <p class="text-3xl font-extrabold tracking-tight text-gray-900">{{ totalSales }}</p>
      </div>

      <div class="bg-white rounded-2xl shadow-xl border border-gray-100 py-5 px-5 flex flex-col gap-1.5">
        <p class="text-xs font-semibold uppercase tracking-widest text-gray-400">{{ t("weekly_pace_label") }}</p>
        <p class="text-3xl font-extrabold tracking-tight text-gray-900">{{ weeklyPace }}</p>
      </div>

      <template v-if="isGrossSales">
        <div class="bg-white rounded-2xl shadow-xl border border-gray-100 py-5 px-5 flex flex-col gap-1.5">
          <p class="text-xs font-semibold uppercase tracking-widest text-gray-400">{{ t("avg_compliance_label") }}</p>
          <p
            class="text-3xl font-extrabold tracking-tight"
            :class="avgCompliance >= complianceThreshold ? 'text-green-700' : 'text-red-600'"
          >
            {{ avgCompliance }} %
          </p>
        </div>

        <div class="bg-white rounded-2xl shadow-xl border border-gray-100 py-5 px-5 flex flex-col gap-1.5">
          <p class="text-xs font-semibold uppercase tracking-widest text-gray-400">
            {{ t("stores_below_label", { threshold: complianceThreshold }) }}
          </p>
          <p class="text-3xl font-extrabold tracking-tight text-red-600">{{ storesBelowThreshold }}</p>
          <p class="text-xs text-red-600">{{ t("stores_below_detail", { total: userStores.length }) }}</p>
        </div>
      </template>

      <div class="bg-white rounded-2xl shadow-xl border border-gray-100 py-5 px-5 flex flex-col gap-1.5">
        <p class="text-xs font-semibold uppercase tracking-widest text-gray-400">{{ t("total_reward_label") }}</p>
        <p class="text-3xl font-extrabold tracking-tight text-gray-900">{{ formatPrice(totalReward) }}</p>
      </div>
    </div>

    <div class="grid grid-cols-[1.4fr_1fr] gap-5">
      <TieredChallengeWeeklyChart :weekly-data="weeklySales" :challenge="challenge" :stores="userStores" />
      <TieredChallengeStoreDistribution :stores="userStores" :challenge="challenge" />
    </div>

    <TieredChallengePilotageStoreRanking
      :stores="allStores"
      :user-store-ids="userStoreIds"
      :counting-method="challenge.countingMethod"
    />
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import { formatPrice } from "@estaly/ui";
import { challengeDays } from "@/utils/challengePeriod";
import TieredChallengeWeeklyChart from "@/components/challenge/tiered/TieredChallengeWeeklyChart.vue";
import TieredChallengeStoreDistribution from "@/components/challenge/tiered/TieredChallengeStoreDistribution.vue";
import TieredChallengePilotageStoreRanking from "@/components/challenge/tiered/TieredChallengePilotageStoreRanking.vue";

import { COMPLIANCE_THRESHOLD, complianceRate, isGrossSalesChallenge } from "@/utils/challengeTiers";

export default {
  components: {
    TieredChallengeWeeklyChart,
    TieredChallengeStoreDistribution,
    TieredChallengePilotageStoreRanking,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  props: {
    challenge: {
      type: Object,
      required: true,
    },
    storeRankings: {
      type: Object,
      required: true,
    },
    userStoreIds: {
      type: Array,
      default: () => [],
    },
    weeklySales: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    allStores() {
      return this.storeRankings.stores || [];
    },

    userStores() {
      if (!this.userStoreIds.length) return this.allStores;
      return this.allStores.filter((s) => this.userStoreIds.includes(s.storeId));
    },

    isGrossSales() {
      return isGrossSalesChallenge(this.challenge);
    },

    complianceThreshold() {
      return COMPLIANCE_THRESHOLD;
    },

    storesWithCompliance() {
      return this.userStores.map((store) => ({
        ...store,
        complianceRate: complianceRate(store.leadsCount, store.salesCount),
      }));
    },

    totalSales() {
      return this.userStores.reduce((sum, s) => sum + (s.salesCount || 0), 0);
    },

    weeklyPace() {
      const { elapsed } = challengeDays(this.challenge);
      if (!elapsed) return 0;
      return Math.round(this.totalSales / Math.max(1, elapsed / 7));
    },

    avgCompliance() {
      const active = this.storesWithCompliance.filter((s) => s.salesCount > 0);
      if (!active.length) return 0;
      const sum = active.reduce((acc, s) => acc + s.complianceRate, 0);
      return Math.round((sum / active.length) * 10) / 10;
    },

    storesBelowThreshold() {
      return this.storesWithCompliance.filter((s) => s.salesCount > 0 && s.complianceRate < COMPLIANCE_THRESHOLD)
        .length;
    },

    totalReward() {
      return this.userStores.reduce((sum, s) => sum + (s.totalReward || 0), 0);
    },
  },

  methods: {
    formatPrice,
  },
};
</script>

<i18n src="./TieredChallengePilotage.i18n.json"></i18n>
